import { useEffect, useMemo, useState } from "react";
import { ListDescriptionSearch } from "../../../../services/PartnerService";
import { normalizeCountryKey, toCanonicalCountryName } from "../../CountryRates/countryCatalog";

type Destination = {
	id: string;
	name: string;
	country: string | null;
};

export default function useFilterCountryCity() {
	const [destinations, setDestinations] = useState<Destination[]>([]);
	const [selectedCountry, setSelectedCountry] = useState<string>("");
	const [selectedDestinationId, setSelectedDestinationId] = useState<string>("");

	useEffect(() => {
		ListDescriptionSearch("").then((res) => {
			if (res) {
				setDestinations(
					res.map((item: any) => ({
						id: item.id,
						name: item.name,
						country: item.country || null,
					}))
				);
			}
		});
	}, []);

	const countryOptions = useMemo(() => {
		const countriesByKey = new Map<string, string>();

		destinations.forEach((destination) => {
			const rawCountry = String(destination.country || "").trim();
			if (!rawCountry) return;

			const countryKey = normalizeCountryKey(rawCountry);
			if (!countryKey || countriesByKey.has(countryKey)) return;

			countriesByKey.set(countryKey, toCanonicalCountryName(rawCountry) || rawCountry);
		});

		const countries = Array.from(countriesByKey.values()).sort((a, b) => a.localeCompare(b));

		return [
			{ value: "", label: "All" },
			...countries.map((countryName) => ({ value: countryName, label: countryName })),
		];
	}, [destinations]);

	const cityOptions = useMemo(() => {
		const filtered = selectedCountry
			? destinations.filter(
					(destination) =>
						normalizeCountryKey(destination.country || "") === normalizeCountryKey(selectedCountry)
			  )
			: destinations;

		return [
			{ value: "", label: "All" },
			...filtered.map((destination) => ({ value: destination.id, label: destination.name })),
		];
	}, [destinations, selectedCountry]);

	const handleCountryChange = (val: any) => {
		setSelectedCountry(val.value);
		setSelectedDestinationId("");
	};

	const handleCityChange = (val: any) => {
		setSelectedDestinationId(val.value);
	};

	return {
		countryOptions,
		cityOptions,
		selectedCountry,
		selectedDestinationId,
		handleCountryChange,
		handleCityChange,
	};
}
